import type { ProductTourDefinition } from '@terra/ui-product-tour';

export const BOOTH_VAULT_TOUR_ID = 'booth-vault';

export type BoothVaultTourGroup = 'discover' | 'signals' | 'attack';

export const BOOTH_VAULT_TOUR_GROUPS: Record<
  BoothVaultTourGroup,
  { labelKey: string }
> = {
  discover: { labelKey: 'phases.discover' },
  signals: { labelKey: 'phases.signals' },
  attack: { labelKey: 'phases.attack' },
};

export type BoothVaultStepContent = {
  titleKey: string;
  bodyKey: string;
  groupId: BoothVaultTourGroup;
};

export const boothVaultStepContent: Record<string, BoothVaultStepContent> = {
  welcome: {
    titleKey: 'steps.welcomeTitle',
    bodyKey: 'steps.welcomeBody',
    groupId: 'discover',
  },
  'start-proxy': {
    titleKey: 'steps.startProxyTitle',
    bodyKey: 'steps.startProxyBody',
    groupId: 'discover',
  },
  'launch-browser': {
    titleKey: 'steps.launchBrowserTitle',
    bodyKey: 'steps.launchBrowserBody',
    groupId: 'discover',
  },
  register: {
    titleKey: 'steps.registerTitle',
    bodyKey: 'steps.registerBody',
    groupId: 'discover',
  },
  'http-history': {
    titleKey: 'steps.httpHistoryTitle',
    bodyKey: 'steps.httpHistoryBody',
    groupId: 'discover',
  },
  signals: {
    titleKey: 'steps.signalsTitle',
    bodyKey: 'steps.signalsBody',
    groupId: 'signals',
  },
  'attack-copilot': {
    titleKey: 'steps.copilotTitle',
    bodyKey: 'steps.copilotBody',
    groupId: 'attack',
  },
  go: {
    titleKey: 'steps.goTitle',
    bodyKey: 'steps.goBody',
    groupId: 'attack',
  },
  'ready-to-hack': {
    titleKey: 'steps.readyToHackTitle',
    bodyKey: 'steps.readyToHackBody',
    groupId: 'attack',
  },
};

export const boothVaultTourDefinition: ProductTourDefinition = {
  id: BOOTH_VAULT_TOUR_ID,
  version: 8,
  legacyStorageKey: 'terra-booth-tour-completed-v8',
  keyboardShortcut: { key: 'b', modifier: true, shift: true },
  steps: [
    {
      id: 'welcome',
      anchorId: null,
      groupId: 'discover',
      presentation: 'center',
      advance: { type: 'manual' },
    },
    {
      id: 'start-proxy',
      anchorId: 'start-proxy',
      groupId: 'discover',
      presentation: 'spotlight',
      advance: { type: 'hostCondition', conditionId: 'proxy-running' },
    },
    {
      id: 'launch-browser',
      anchorId: 'launch-browser',
      actionAnchorId: 'launch-browser-action',
      groupId: 'discover',
      presentation: 'spotlight',
      advance: { type: 'anchorClick' },
    },
    {
      id: 'register',
      anchorId: null,
      groupId: 'discover',
      presentation: 'center',
      advance: { type: 'hostCondition', conditionId: 'vault-registered' },
    },
    {
      id: 'http-history',
      anchorId: 'requests',
      groupId: 'discover',
      presentation: 'coachmark',
      advance: { type: 'manual' },
      enterEffectId: 'sidebar:requests',
      cardPlacement: 'beside',
    },
    {
      id: 'signals',
      anchorId: 'signals',
      groupId: 'signals',
      presentation: 'coachmark',
      advance: { type: 'manual' },
      enterEffectId: 'sidebar:signals',
      cardPlacement: 'beside',
    },
    {
      id: 'attack-copilot',
      anchorId: 'attack-with-copilot',
      groupId: 'attack',
      presentation: 'spotlight',
      advance: { type: 'anchorClick' },
    },
    {
      id: 'go',
      anchorId: 'send-chat-message',
      groupId: 'attack',
      presentation: 'spotlight',
      advance: { type: 'anchorClick' },
      cardPlacement: 'above',
    },
    {
      id: 'ready-to-hack',
      anchorId: null,
      groupId: 'attack',
      presentation: 'center',
      advance: { type: 'manual' },
    },
  ],
};

export const BOOTH_VAULT_TOUR_STORAGE_KEY =
  boothVaultTourDefinition.legacyStorageKey ?? 'terra-booth-tour-completed-v4';
